<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRoute } from 'vue-router';
import api from '@/lib/api.js';
import * as XLSX from 'xlsx';

const route = useRoute();

// 학교 결과 데이터
const schoolData = ref(null);
const loading = ref(true);

// API 호출 함수
const fetchSchoolResult = async () => {
  try {
    loading.value = true;
    const { exam_id, schul_code } = route.query;
    
    if (!exam_id || !schul_code) {
      throw new Error('필수 파라미터가 없습니다.');
    }

    console.log('Analysis - School 결과 API 요청:', {
      exam_id,
      schul_code
    });

    const response = await api.post('/exam/tea/resultLitSchoolDetail', {
      exam_id,
      schul_code
    });

    if (response.data.success) {
      schoolData.value = response.data.result;
      console.log('Analysis - School 결과 데이터:', schoolData.value);
    } else {
      throw new Error(response.data.message || '데이터를 가져오는데 실패했습니다.');
    }
  } catch (error) {
    console.error('Analysis - School 결과 데이터 조회 실패:', error);
  } finally {
    loading.value = false;
  }
};

// 학교 전체 통계 계산
const totalSchoolStats = computed(() => {
  if (!schoolData.value?.classList) {
    return {
      totalStudents: 0,
      total_field4_cnt: 0,
      total_field3_cnt:0,
      total_field2_cnt: 0,
      total_field1_cnt: 0,
      total_field4_male_cnt: 0,
      total_field3_male_cnt: 0,
      total_field2_male_cnt: 0,
      total_field1_male_cnt: 0,
      total_field1_female_cnt: 0,
      total_field2_female_cnt: 0,
      total_field3_female_cnt: 0,
      total_field4_female_cnt: 0,
      korLevel3Sum: 0,
      korLevel4Sum: 0,
      matLevel1Sum: 0,
      matLevel2Sum: 0,
      matLevel3Sum: 0,
      matLevel4Sum: 0,
      field1Rate: 0,
      field2Rate: 0,
      field3Rate: 0,
      field4Rate: 0
    };
  }

  const stats = schoolData.value.classList.reduce((acc, item) => {
    acc.totalStudents += item.total_cnt || 0;
    acc.total_field4_cnt += (item.female_level4_cnt || 0) + (item.male_level4_cnt || 0);
    acc.total_field3_cnt += (item.female_level3_cnt || 0) + (item.male_level3_cnt || 0);
    acc.total_field2_cnt += (item.female_level2_cnt || 0) + (item.male_level2_cnt || 0);
    acc.total_field1_cnt += (item.female_level1_cnt || 0) + (item.male_level1_cnt || 0);
    acc.total_field4_male_cnt += (item.male_level4_cnt || 0) 
    acc.total_field3_male_cnt += (item.male_level3_cnt || 0)
    acc.total_field2_male_cnt += (item.male_level2_cnt || 0)
    acc.total_field1_male_cnt += (item.male_level1_cnt || 0)
    acc.total_field1_female_cnt += item.female_level1_cnt || 0;
    acc.total_field2_female_cnt += item.female_level2_cnt || 0;
    acc.total_field3_female_cnt += item.female_level3_cnt || 0;
    acc.total_field4_female_cnt += item.female_level4_cnt || 0;
    acc.matLevel4Sum += item.matlevel4 || 0;
    return acc;
  }, {
    totalStudents: 0,
    total_field4_cnt: 0,
    total_field3_cnt:0,
    total_field2_cnt: 0,
    total_field1_cnt: 0,
    total_field4_male_cnt: 0,
    total_field3_male_cnt: 0,
    total_field2_male_cnt: 0,
    total_field1_male_cnt: 0,
    total_field1_female_cnt: 0,
    total_field2_female_cnt: 0,
    total_field3_female_cnt: 0,
    total_field4_female_cnt: 0,
    matLevel1Sum: 0,
    matLevel2Sum: 0,
    matLevel3Sum: 0,
    matLevel4Sum: 0,
    // field 값들은 고정값이므로 첫 번째 항목에서 가져옴
    field1Rate: schoolData.value.classList[0]?.total_field1_rate || 0,
    field2Rate: schoolData.value.classList[0]?.total_field2_rate || 0,
    field3Rate: schoolData.value.classList[0]?.total_field3_rate || 0,
    field4Rate: schoolData.value.classList[0]?.total_field4_rate || 0
  });

  return stats;
});

// 엑셀 다운로드 함수
const downloadExcel = () => {
  if (!schoolData.value?.classList) {
    alert('다운로드할 데이터가 없습니다.');
    return;
  }

  const workbook = XLSX.utils.book_new();

  const headers = [
    '반', '응시자', 'AI 디지털 기술', 'AI 디지털 활용', 'AI 디지털 윤리', '인간중심 AI 설계',
    '4수준(전체)', '4수준(남자)', '4수준(여자)', 
    '3수준(전체)', '3수준(남자)', '3수준(여자)', 
    '2수준(전체)', '2수준(남자)', '2수준(여자)', 
    '1수준(전체)', '1수준(남자)', '1수준(여자)'
  ];
  
  // 전체 통계 행 추가
  const totalRow = [
    '전체', 
    totalSchoolStats.value.totalStudents,
    Math.round((totalSchoolStats.value.field1Rate || 0) * 10) / 10,
    Math.round((totalSchoolStats.value.field2Rate || 0) * 10) / 10,
    Math.round((totalSchoolStats.value.field3Rate || 0) * 10) / 10,
    Math.round((totalSchoolStats.value.field4Rate || 0) * 10) / 10,
    totalSchoolStats.value.total_field4_cnt,
    totalSchoolStats.value.total_field4_male_cnt,
    totalSchoolStats.value.total_field4_female_cnt,
    totalSchoolStats.value.total_field3_cnt,
    totalSchoolStats.value.total_field3_male_cnt,
    totalSchoolStats.value.total_field3_female_cnt,
    totalSchoolStats.value.total_field2_cnt,
    totalSchoolStats.value.total_field2_male_cnt,
    totalSchoolStats.value.total_field2_female_cnt,
    totalSchoolStats.value.total_field1_cnt,
    totalSchoolStats.value.total_field1_male_cnt,
    totalSchoolStats.value.total_field1_female_cnt
  ];

  const excelData = schoolData.value.classList.map(item => [
    `${item.grade_code}-${item.school_cls}`,
    item.total_cnt || 0,
    Math.round((item.field1_rate || 0) * 10) / 10,
    Math.round((item.field2_rate || 0) * 10) / 10,
    Math.round((item.field3_rate || 0) * 10) / 10,
    Math.round((item.field4_rate || 0) * 10) / 10,
    (item.female_level4_cnt || 0) + (item.male_level4_cnt || 0),
    item.male_level4_cnt || 0,
    item.female_level4_cnt || 0,
    (item.female_level3_cnt || 0) + (item.male_level3_cnt || 0),
    item.male_level3_cnt || 0,
    item.female_level3_cnt || 0,
    (item.female_level2_cnt || 0) + (item.male_level2_cnt || 0),
    item.male_level2_cnt || 0,
    item.female_level2_cnt || 0,
    (item.female_level1_cnt || 0) + (item.male_level1_cnt || 0),
    item.male_level1_cnt || 0,
    item.female_level1_cnt || 0
  ]);

  const sheetData = [headers, totalRow, ...excelData];
  const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
  XLSX.utils.book_append_sheet(workbook, worksheet, '반별결과');
  const schulName = schoolData.value.classList[0]?.schul_nm || '학교';
  const fileName = `${schulName}_반별진단결과_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.xlsx`;
  XLSX.writeFile(workbook, fileName);
};

onMounted(() => {
  fetchSchoolResult();
});
</script>

<template>
  <div class="table-container">
    <div class="table-top">
      <div class="title pb-4">
        <h5>우리 학교 반별 진단 결과</h5>
        <p class="download-button" @click="downloadExcel">반별 진단 결과 엑셀 다운로드</p>
      </div>         
    </div>
    <table>
      <thead>
        <tr>
          <th rowspan="3">반</th>
          <th rowspan="3">응시자</th>
          <th colspan="4">하위 영역별 정답률</th>
          <th colspan="3">4수준 학생 수</th>
          <th colspan="3">3수준 학생수</th>
          <th colspan="3">2수준 학생수</th>
          <th colspan="3">1수준 학생수</th>
        </tr>
        <tr>
          <th>AI 디지털 기술</th>
          <th>AI 디지털 활용</th>
          <th>AI 디지털 윤리</th>          
          <th>인간중심 AI 설계</th>
          <th>전체</th>
          <th>남자</th>
          <th>여자</th>  
          <th>전체</th>
          <th>남자</th>
          <th>여자</th>          
         <th>전체</th>
          <th>남자</th>
          <th>여자</th>
         <th>전체</th>
          <th>남자</th>
          <th>여자</th>  
        </tr>        
      </thead>
      <tbody>
        <tr v-if="loading">
          <td colspan="18" class="text-center">로딩 중...</td>
        </tr>
        <tr v-else-if="!schoolData?.classList?.length">
          <td colspan="18" class="text-center">데이터가 없습니다.</td>
        </tr>
        <template v-else>
          <tr class="total-row">
            <td>전체</td>
            <td>{{ totalSchoolStats.totalStudents }}</td>
            <td>{{ Math.round((totalSchoolStats.field1Rate || 0) * 10) / 10 }}</td>
            <td>{{ Math.round((totalSchoolStats.field2Rate || 0) * 10) / 10 }}</td>
            <td>{{ Math.round((totalSchoolStats.field3Rate || 0) * 10) / 10 }}</td>
            <td>{{ Math.round((totalSchoolStats.field4Rate || 0) * 10) / 10 }}</td>
            <td>{{ totalSchoolStats.total_field4_cnt }}</td>
            <td>{{ totalSchoolStats.total_field4_male_cnt }}</td>
            <td>{{ totalSchoolStats.total_field4_female_cnt }}</td>
            <td>{{ totalSchoolStats.total_field3_cnt }}</td>
            <td>{{ totalSchoolStats.total_field3_male_cnt }}</td>
            <td>{{ totalSchoolStats.total_field3_female_cnt }}</td>
            <td>{{ totalSchoolStats.total_field2_cnt }}</td>
            <td>{{ totalSchoolStats.total_field2_male_cnt }}</td>
            <td>{{ totalSchoolStats.total_field2_female_cnt }}</td>
            <td>{{ totalSchoolStats.total_field1_cnt }}</td>
            <td>{{ totalSchoolStats.total_field1_male_cnt }}</td>
            <td>{{ totalSchoolStats.total_field1_female_cnt }}</td>
          </tr>
          <tr v-for="(item, index) in schoolData.classList" :key="index">
            <td>{{ item.grade_code }}-{{ item.school_cls }}</td>
            <td>{{ item.total_cnt }}</td>
            <td>{{ Math.round((item.field1_rate || 0) * 10) / 10 }}</td>
            <td>{{ Math.round((item.field2_rate || 0) * 10) / 10 }}</td>
            <td>{{ Math.round((item.field3_rate || 0) * 10) / 10 }}</td>
            <td>{{ Math.round((item.field4_rate || 0) * 10) / 10 }}</td>
            <td>{{ (item.female_level4_cnt || 0) + (item.male_level4_cnt || 0) }}</td>
            <td>{{ item.male_level4_cnt || 0 }}</td>
            <td>{{ item.female_level4_cnt || 0 }}</td>
            <td>{{ (item.female_level3_cnt || 0) + (item.male_level3_cnt || 0) }}</td>
            <td>{{ item.male_level3_cnt || 0 }}</td>
            <td>{{ item.female_level3_cnt || 0 }}</td>
            <td>{{ (item.female_level2_cnt || 0) + (item.male_level2_cnt || 0) }}</td>
            <td>{{ item.male_level2_cnt || 0 }}</td>
            <td>{{ item.female_level2_cnt || 0 }}</td>
            <td>{{ (item.female_level1_cnt || 0) + (item.male_level1_cnt || 0) }}</td>
            <td>{{ item.male_level1_cnt || 0 }}</td>
            <td>{{ item.female_level1_cnt || 0 }}</td>
          </tr>
        </template>
      </tbody>
    </table>
  </div>
</template>

<style lang="scss" scoped>
.table-container {
    background-color: #fff;
    padding: 20px 20px 30px 20px;
    border-radius: 15px;
}
.table-top {
  display: flex;
  flex-direction: column;
  .title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }
  .download-button {
    background-color: var(--smoe-primary-500);
    color: #fff;
    border-radius: 5px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 10px;
    cursor: pointer;
    font-size: 16px;
    font-weight: 500;
    height: 40px;
    line-height: 40px;
  }
}

/* 테이블 기본 스타일 */
table {
  width: 100%;
  border-collapse: collapse;
  text-align: center;
  font-size: 16px;
}
th, td {
  border: 1px solid #ccc; 
  padding: 8px 4px; 
  vertical-align: middle;
}
th {
  background-color: #f2f2f2; 
  font-weight: 500;
}
td {
  text-align: center;
}

.total-row {
  background-color: #e3f2fd !important;
  font-weight: 600;
  
  td {
    background-color: #e3f2fd !important;
  }
}
.result-container table tr th {
    height: 60px;
    border-right: 1px solid #ccc;
    text-align: center;
    padding: 0px;
}
</style>